import {
  BREAKPOINTS,
  Button,
  ButtonProps,
  ImageSource,
  ImageSourceSizes,
  PlusToMinusIcon,
  Spinner,
  Typography,
  TypographyProps,
  getImageSizes
} from '@ag1/ui';
import { CommonSectionProps } from '@portal/shared/studio/types';
import type { Offer } from '@portal/types/offer';
import { parseImageAssetId } from '@sanity/asset-utils';
import clsx, { ClassValue } from 'clsx';
import { Theme } from 'constants/theme';
import { NextRouter, useRouter } from 'next/router';
import type { CustomContent } from 'processes/DynamicLanders/types';
import { usePageContext } from 'processes/Page/PageContext';
import { useCallback, useMemo } from 'react';
import { Collapse } from 'react-collapse';
import { PortableTextData } from 'schema/studio/common/portableText';
import { CustomPortableText } from 'shared/studio/PortableText';
import { UnifiedButtonType } from 'shared/studio/UnifiedButton/UnifiedButton.types';
import { getThemeCSSClass } from 'shared/utils/getThemeCSSClass';
import { isPreviewRouteView } from 'shared/utils/previewRouteHelpers';
import { urlFor } from 'shared/utils/urlFor';
import { useOfferButtonDisabledReason } from './Offer/common';
import styles from './styles.module.scss';

export const PREVIEW_PLACEHOLDER_QUERY_PARAM = 'placeholders';

export const shouldShowContentBlockPlaceholders = (router: NextRouter) => {
  // Never show placeholders if we're not in a preview route.
  if (!isPreviewRouteView(router)) {
    return false;
  }

  // Check our query param to see if we should show placeholders.
  const param = router.query[PREVIEW_PLACEHOLDER_QUERY_PARAM];
  if (param !== undefined) {
    return `${param}` === 'true' || `${param}` === '1';
  }

  return true;
};

type ContentBlockSlug = string | undefined;

export interface ContentBlockProps extends CommonSectionProps {
  /** Not present for /landers/preview/ routes. */
  slug: ContentBlockSlug;
  customContent: CustomContent[];
}

export interface CustomListItem {
  header: string;
  body: string;
}

export interface CustomOfferListItem {
  text: string;
  hide?: boolean;
}

export interface CustomOfferPricesItem {
  text: string;
  description: string;
  price: number;
  shopifyVariantId?: string;
  quantity?: number;
  hide?: boolean;
}

export interface CustomGalleryItem {
  image: string;
  description: string;
}

export interface CustomButtonFields {
  label: string;
  link: string;
}

export interface CustomCaptionFields {
  text: string;
}

export const EMPTY_CONTENT_BLOCK_IMAGE = '#';

const CONTENT_BACKGROUND_COLORS = ['white', 'grey', 'stone'] as const;
export type ContentBackgroundColor = (typeof CONTENT_BACKGROUND_COLORS)[number];
export const isContentBackgroundColor = (color: string | null): color is ContentBackgroundColor =>
  CONTENT_BACKGROUND_COLORS.includes(color as ContentBackgroundColor);

const PADDING_SIZES = ['none', 'small', 'medium', 'large'] as const;
export type ContentPaddingSize = (typeof PADDING_SIZES)[number];
export const isContentPaddingSize = (size: string | null): size is ContentPaddingSize =>
  PADDING_SIZES.includes(size as ContentPaddingSize);

const HEADLINE_SIZES = ['2', '3'] as const;
export type ContentHeadlineSize = (typeof HEADLINE_SIZES)[number];
export const isContentHeadlineSize = (size: string | null): size is ContentHeadlineSize =>
  HEADLINE_SIZES.includes(size as ContentHeadlineSize);

export const IMAGE_POSITIONS = ['left', 'right'] as const;
export type ImagePosition = (typeof IMAGE_POSITIONS)[number];
export const isImagePosition = (position: string | null): position is ImagePosition =>
  IMAGE_POSITIONS.includes(position as ImagePosition);

const ITEM_FLIP_STATES = ['default', 'flip'] as const;
type ItemFlipState = (typeof ITEM_FLIP_STATES)[number];
const isItemFlipState = (state: string | null): state is ItemFlipState =>
  ITEM_FLIP_STATES.includes(state as ItemFlipState);

const parseItemFlipState = (content: string | null): ItemFlipState =>
  isItemFlipState(content) ? content : 'default';
const parseContentBlockToggle = (i: string) => i === '1' || i === 'true';

const getComplexContentObjectArray = <T extends Record<string, any>>(
  content: string | null | undefined
): Partial<T>[] | null => {
  if (typeof content === 'string' && content.startsWith('[') && content.endsWith(']')) {
    return JSON.parse(content);
  }
  if (Array.isArray(content)) {
    return content;
  }
  return null;
};

const getComplexContentObject = <T extends Record<string, any>>(
  content: any
): (T extends any[] ? never : Partial<T>) | null => {
  if (typeof content === 'string' && content.startsWith('{') && content.endsWith('}')) {
    return JSON.parse(content);
  }
  if (typeof content === 'object' && !Array.isArray(content)) {
    return content;
  }
  return null;
};

export const getSanityImageFromString = (ref: string) => {
  if (!ref) {
    return null;
  }

  const { url } = urlFor({ asset: { _ref: ref, _type: 'reference' } });
  const { width, height } = parseImageAssetId(ref);

  return {
    src: url,
    width,
    height
  };
};

type ResponsiveContentBlockImageType = {
  desktop: string | null;
  mobile: string | null;
};
export const getResponsiveSanityImage = (
  image: ResponsiveContentBlockImageType | null | undefined
) => {
  const result: {
    [Key in keyof NonNullable<typeof image>]?: ReturnType<typeof getSanityImageFromString>;
  } = {};

  if (!image) {
    return result;
  }

  // eslint-disable-next-line no-restricted-syntax
  for (const [key, rawSrc] of Object.entries(image)) {
    if (rawSrc) {
      const data = getSanityImageFromString(rawSrc);
      if (data) {
        result[key as keyof typeof image] = data;
      }
    }
  }
  return result;
};

export const useSanityImageFromCustomContent = (
  customContent: CustomContent[],
  {
    slug,
    variantKey = ''
  }: {
    slug: ContentBlockSlug;
    /**
     * Provide if searching for a specific "key". Only needed for custom
     * sections that contain multiple images.
     */
    variantKey?: string;
  }
) =>
  useMemo(() => {
    const imageContent = customContent.find((cc) => {
      if (slug && cc.section_slug !== slug) {
        return false;
      }
      const types = cc.content_type?.split('+');
      if (!types || types[0] !== 'media') {
        return false;
      }

      // Ignore types that include mobile-toggle, as they're used to hide/show a
      // separate mobile media form field and only contain boolean data.
      if (types.length > 2 && types[2] === 'mobile-toggle') {
        return false;
      }

      if (variantKey.length) {
        return types[1] === variantKey;
      }
      return true;
    })?.content;

    if (imageContent) {
      return getSanityImageFromString(imageContent);
    }
    return null;
  }, [customContent, slug, variantKey]);

export function getPlaceholderForPreview<T extends any>(
  router: NextRouter,
  placeholder: T,
  fallback: T
): T;
export function getPlaceholderForPreview<T extends any>(
  router: NextRouter,
  placeholder: T,
  fallback?: null
): T | typeof fallback;
export function getPlaceholderForPreview<T extends any = null>(
  router: NextRouter,
  placeholder: T,
  fallback: T | null = null
) {
  if (shouldShowContentBlockPlaceholders(router)) {
    return placeholder;
  }
  return fallback;
}

type ContentBlockFieldOptions = {
  slug: ContentBlockSlug;
} & (
  | {
      router: NextRouter;
      previewPlaceholder: string;
    }
  | {
      router?: never;
      previewPlaceholder?: never;
    }
);

type FieldOptionsWithParser<T> = { slug: ContentBlockSlug; parser: (content: string | null) => T };
type FieldOptionsWithFallback<T> = { slug: ContentBlockSlug; fallback: T };
type FieldOptionsWithRouter = { slug: ContentBlockSlug; router: NextRouter };

const getContentBlockField = (
  customContent: CustomContent[],
  type: string,
  { slug, router, previewPlaceholder }: ContentBlockFieldOptions
) => {
  const content = customContent.find((cc) => {
    if (cc.content_type !== type) {
      return false;
    }
    if (slug) {
      return cc.section_slug === slug;
    }
    return true;
  })?.content;

  if (router && previewPlaceholder) {
    return content || getPlaceholderForPreview(router, previewPlaceholder, '');
  }
  return content || null;
};

export const useContentBlockField = (
  customContent: CustomContent[],
  type: string,
  options: ContentBlockFieldOptions
) => {
  return useMemo(
    () =>
      getContentBlockField(
        customContent,
        type,
        options.router
          ? {
              slug: options.slug,
              router: options.router,
              previewPlaceholder: options.previewPlaceholder
            }
          : { slug: options.slug }
      ),
    [customContent, options.slug, options.previewPlaceholder, options.router, type]
  );
};

export const useContentBlockFieldParser = <T extends unknown>(
  customContent: CustomContent[],
  type: string,
  { slug, parser }: FieldOptionsWithParser<T>
) =>
  useMemo(
    () => parser(getContentBlockField(customContent, type, { slug })),
    [customContent, type, slug, parser]
  );

export const useContentBlockListItems = (
  customContent: CustomContent[],
  { slug, router }: FieldOptionsWithRouter
) =>
  useMemo<CustomListItem[]>(() => {
    const content =
      getComplexContentObjectArray<CustomListItem>(
        getContentBlockField(customContent, 'custom-list', { slug })
      ) || [];

    // Add a placeholder item for preview routes
    if (!content.length && shouldShowContentBlockPlaceholders(router)) {
      content.push({ header: 'Header 1', body: 'Body content' } satisfies CustomListItem);
    }

    return content.reduce((acc, i, index) => {
      const item = {
        header: i.header || getPlaceholderForPreview(router, `Header ${index + 1}`, ''),
        body: i.body || getPlaceholderForPreview(router, 'Body content', '')
      };
      if (item.header && item.body) {
        acc.push(item);
      }
      return acc;
    }, [] as CustomListItem[]);
  }, [customContent, router, slug]);

export const useContentBlockOfferListItems = (
  customContent: CustomContent[],
  { slug, router }: FieldOptionsWithRouter
) =>
  useMemo<CustomOfferListItem[]>(() => {
    const content =
      getComplexContentObjectArray<CustomOfferListItem>(
        getContentBlockField(customContent, 'offer-list', { slug })
      ) || [];

    // Add a placeholder item for preview routes
    if (!content.length && shouldShowContentBlockPlaceholders(router)) {
      content.push({ text: 'Item 1' } satisfies CustomOfferListItem);
    }

    return content.reduce((acc, i, index) => {
      if (i.hide) {
        return acc;
      }

      const item = {
        text: i.text || getPlaceholderForPreview(router, `Text ${index + 1}`, '')
      };
      if (item.text) {
        acc.push(item);
      }
      return acc;
    }, [] as CustomOfferListItem[]);
  }, [customContent, router, slug]);

export const useContentBlockOfferPricesItems = (
  customContent: CustomContent[],
  { slug, router }: FieldOptionsWithRouter
) =>
  useMemo<CustomOfferPricesItem[]>(() => {
    const content =
      getComplexContentObjectArray<CustomOfferPricesItem>(
        getContentBlockField(customContent, 'offer-prices', { slug })
      ) || [];

    // Add a placeholder item for preview routes
    if (!content.length && shouldShowContentBlockPlaceholders(router)) {
      content.push(
        // "Primary" items are shown on top.
        {
          text: 'Primary Product',
          description: 'This is a description of the primary product.',
          price: 79
        } satisfies CustomOfferPricesItem,
        // These are shown below the primary item.
        {
          text: 'Add-on Product',
          description: 'This is included with your purchase at no extra cost.',
          price: 19
        } satisfies CustomOfferPricesItem
      );
    }

    return content.reduce((acc, i, index) => {
      if (i.hide) {
        return acc;
      }

      const item: CustomOfferPricesItem = {
        text: i.text || getPlaceholderForPreview(router, `Text ${index + 1}`, ''),
        description: i.description || getPlaceholderForPreview(router, 'Description', ''),
        price: i.price || 0,
        quantity: i.quantity || 1,
        shopifyVariantId: i.shopifyVariantId
      };
      if (item.text) {
        acc.push(item);
      }
      return acc;
    }, [] as CustomOfferPricesItem[]);
  }, [customContent, router, slug]);

export const useContentBlockGalleryItems = (
  customContent: CustomContent[],
  { slug, router }: FieldOptionsWithRouter
) =>
  useMemo<CustomGalleryItem[]>(() => {
    const content =
      getComplexContentObjectArray<CustomGalleryItem>(
        getContentBlockField(customContent, 'custom-gallery', { slug })
      ) || [];

    if (!content.length && shouldShowContentBlockPlaceholders(router)) {
      content.push({
        image: EMPTY_CONTENT_BLOCK_IMAGE,
        description: 'Description'
      } satisfies CustomGalleryItem);
    }

    return content.reduce((acc, i) => {
      const item = {
        image: i.image || getPlaceholderForPreview(router, EMPTY_CONTENT_BLOCK_IMAGE, ''),
        description: i.description || getPlaceholderForPreview(router, 'Description', '')
      } satisfies CustomGalleryItem;
      if (item.image && item.description) {
        acc.push(item);
      }
      return acc;
    }, [] as CustomGalleryItem[]);
  }, [customContent, router, slug]);

export const useContentBlockItemFlip = (customContent: CustomContent[], slug: ContentBlockSlug) =>
  useContentBlockFieldParser(customContent, 'custom-item-flip', {
    slug,
    parser: parseItemFlipState
  });

export const useContentBlockImagePosition = (
  customContent: CustomContent[],
  { slug, fallback }: FieldOptionsWithFallback<ImagePosition>
) => {
  const parser = useCallback((i: string) => (isImagePosition(i) ? i : fallback), [fallback]);
  const position = useContentBlockFieldParser(customContent, 'img-pos', {
    slug,
    parser
  });
  return { position, flipped: position !== fallback };
};

export const useContentBlockBackgroundColor = (
  customContent: CustomContent[],
  { slug, fallback }: FieldOptionsWithFallback<ContentBackgroundColor>
) => {
  const parser = useCallback(
    (i: string) => (isContentBackgroundColor(i) ? i : fallback),
    [fallback]
  );
  return useContentBlockFieldParser(customContent, 'bg-color', {
    slug,
    parser
  });
};

export const useContentBlockPaddingTop = (
  customContent: CustomContent[],
  { slug, fallback }: FieldOptionsWithFallback<ContentPaddingSize>
) => {
  const parser = useCallback((i: string) => (isContentPaddingSize(i) ? i : fallback), [fallback]);
  return useContentBlockFieldParser(customContent, 'paddingTop', {
    slug,
    parser
  });
};

export const useContentBlockPaddingBottom = (
  customContent: CustomContent[],
  { slug, fallback }: FieldOptionsWithFallback<ContentPaddingSize>
) => {
  const parser = useCallback((i: string) => (isContentPaddingSize(i) ? i : fallback), [fallback]);
  return useContentBlockFieldParser(customContent, 'paddingBottom', {
    slug,
    parser
  });
};

export const useContentBlockHeadlineSize = (
  customContent: CustomContent[],
  { slug, fallback }: FieldOptionsWithFallback<ContentHeadlineSize>
) => {
  const parser = useCallback((i: string) => (isContentHeadlineSize(i) ? i : fallback), [fallback]);
  return useContentBlockFieldParser(customContent, 'headline-size', {
    slug,
    parser
  });
};

export const useContentBlockToggle = (
  customContent: CustomContent[],
  type: string,
  { slug }: { slug: ContentBlockSlug }
) =>
  useContentBlockFieldParser(customContent, type, {
    slug,
    parser: parseContentBlockToggle
  });

export const useContentBlockButton = (
  customContent: CustomContent[],
  { slug, router }: FieldOptionsWithRouter
): CustomButtonFields | null =>
  useMemo(() => {
    const rawContent = getContentBlockField(customContent, 'custom-button', { slug });
    const content = getComplexContentObject<CustomButtonFields>(rawContent);
    if (!content || !content.label || !content.link) {
      return shouldShowContentBlockPlaceholders(router)
        ? ({
            label: content?.label || getPlaceholderForPreview(router, 'Button', ''),
            link: content?.link || getPlaceholderForPreview(router, '#', '')
          } satisfies CustomButtonFields)
        : null;
    }
    return {
      label: content.label,
      link: content.link
    } satisfies CustomButtonFields;
  }, [customContent, router, slug]);

export const useContentBlockCaptions = (
  customContent: CustomContent[],
  { slug, router }: FieldOptionsWithRouter
) => {
  return useMemo<CustomCaptionFields[]>(() => {
    const content =
      getComplexContentObjectArray<CustomCaptionFields>(
        getContentBlockField(customContent, 'captions', { slug })
      ) || [];

    return content.reduce((acc, i, index) => {
      const item = {
        text: i.text || getPlaceholderForPreview(router, `Caption ${index + 1}`, '')
      };
      if (item.text) {
        acc.push(item);
      }
      return acc;
    }, [] as CustomCaptionFields[]);
  }, [customContent, router, slug]);
};

export const useOfferContentBlock = (
  customContent: CustomContent[],
  { slug, router }: FieldOptionsWithRouter,
  offer: Offer | null
) => {
  const headline = useContentBlockField(customContent, 'custom-headline', {
    slug,
    router,
    previewPlaceholder: 'Headline'
  });
  const backgroundColor = useContentBlockBackgroundColor(customContent, {
    slug,
    fallback: 'white'
  });
  const imagePosition = useContentBlockImagePosition(customContent, { slug, fallback: 'right' });
  const invalidReason = useOfferButtonDisabledReason(offer);
  let buttonLabel = useContentBlockField(customContent, 'button-label', {
    slug,
    router,
    previewPlaceholder: 'Get Your AG1'
  });
  const buttonIconPosition: ButtonProps['iconPosition'] = 'right';
  let buttonIcon: string | undefined;
  if (invalidReason) {
    buttonLabel = 'See Other Offers';
    buttonIcon = 'arrowRight';
  }

  const showTrumedNote = useContentBlockToggle(customContent, 'trumed-note', { slug });
  const captions = useContentBlockCaptions(customContent, { slug, router });
  const showOldPriceFirst = useContentBlockToggle(customContent, 'old-price-first', { slug });

  const theme: Theme = 'new';

  return {
    headline,
    backgroundColor,
    imagePosition,
    invalidReason,
    buttonLabel,
    buttonIcon,
    buttonIconPosition,
    showTrumedNote,
    showOldPriceFirst,
    captions,
    theme
  };
};

// ### Components
const makePortableTextBlock = (text: string) =>
  [
    {
      children: [
        {
          _type: 'span',
          _key: text,
          text
        }
      ],
      _type: 'block' as 'block',
      style: 'normal'
    }
  ] satisfies PortableTextData;

export const FormattedTextBlock: React.FC<{
  className?: string;
  variant?: 'bodySmall';
  text: string;
}> = ({ className, text, variant = 'bodySmall' }) => {
  const textValue = useMemo(() => makePortableTextBlock(text), [text]);

  return <CustomPortableText className={clsx(className, styles[variant])} value={textValue} />;
};

export const ListItem: React.FC<{
  item: CustomListItem;
  isOpen: boolean;
  onToggle: () => void;
  useBorderTop?: boolean;
  smallPadding?: boolean;
  headerTypographyProps: TypographyProps;
}> = ({
  item: { header, body },
  isOpen: isOpenProp,
  onToggle,
  useBorderTop,
  smallPadding,
  headerTypographyProps
}) => {
  const router = useRouter();
  const isOpen = useMemo(() => isOpenProp || isPreviewRouteView(router), [router, isOpenProp]);

  return (
    <div
      className={clsx(
        styles.listItem,
        useBorderTop && styles.topBorder,
        smallPadding && styles.smallPadding
      )}>
      <div className={styles.row}>
        <div
          className={styles.listToggle}
          role="button"
          data-gtm-skip-global
          onClick={onToggle}
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              onToggle();
            }
          }}>
          <Typography component="h4" weight="700" noPadding {...headerTypographyProps}>
            {header}
          </Typography>
          <PlusToMinusIcon isOpen={isOpen} className={styles.icon} />
        </div>
      </div>
      <Collapse isOpened={isOpen}>
        <Typography className={styles.text} variant="body" size="1" component="div" noPadding>
          {body}
        </Typography>
      </Collapse>
    </div>
  );
};

export const ImagePlaceholder: React.FC<{ className?: string; aspectRatio?: number }> = ({
  className,
  aspectRatio = 1
}) => (
  <div
    className={clsx(styles.imagePlaceholder, className)}
    style={{
      paddingTop: `${100 / aspectRatio}%`
    }}
  />
);

export const ResponsiveContentBlockImage: React.FC<{
  image: ResponsiveContentBlockImageType | null | undefined;
  className?: string;
  sizes?: ImageSourceSizes;
  eagerLoading?: boolean;
}> = ({ image, className, sizes: originalSizes, eagerLoading = false }) => {
  const { desktop, mobile } = useMemo(() => getResponsiveSanityImage(image), [image]);
  const sizes = getImageSizes(originalSizes);

  return !desktop && !mobile ? (
    <ImagePlaceholder />
  ) : (
    <picture>
      {desktop && (
        <ImageSource
          {...desktop}
          media={mobile ? `(min-width:${BREAKPOINTS.s}px)` : ''}
          sizes={sizes}
        />
      )}
      {mobile && <ImageSource {...mobile} sizes={sizes} />}
      <img
        className={clsx(styles.image, className)}
        loading={eagerLoading ? 'eager' : 'lazy'}
        alt=""
        fetchPriority={eagerLoading ? 'high' : undefined}
      />
    </picture>
  );
};

type ContentBlockButtonCustomization = Pick<
  ButtonProps,
  'size' | 'onClick' | 'variant' | 'icon' | 'iconPosition' | 'theme'
> &
  Pick<UnifiedButtonType, 'hoverColor'> & {
    isLoading?: boolean;
    isDisabled?: boolean;
  };

export const ContentBlockButton: React.FC<{
  className?: ClassValue;
  button: Omit<CustomButtonFields, 'link'> & { link?: string | null };
  fluid?: boolean;
  customization?: ContentBlockButtonCustomization;
  ignoreLinkDisable?: boolean;
}> = ({
  className,
  button: { label, link: linkProp },
  fluid,
  customization,
  ignoreLinkDisable
}) => {
  const { shouldDisableCTALink, maybeReplaceOfferlessCheckoutLink } = usePageContext();

  // Transform old custom content build-a-bundle links to new offer links when
  // applicable.
  const link = useMemo(
    () => maybeReplaceOfferlessCheckoutLink(linkProp),
    [linkProp, maybeReplaceOfferlessCheckoutLink]
  );

  const disableLink = useMemo(
    () => !ignoreLinkDisable && shouldDisableCTALink(link, { noEmpty: true }),
    [ignoreLinkDisable, link, shouldDisableCTALink]
  );

  if (customization) {
    const { size, onClick, variant, icon, iconPosition, theme, hoverColor, isLoading, isDisabled } =
      customization;

    const hasAction = !!onClick || !!link;
    const disabled = isLoading || isDisabled || disableLink || !hasAction;

    return (
      <Button
        className={clsx(
          className,
          variant === 'primary' && hoverColor === 'dark' && styles.dark,
          getThemeCSSClass(theme)
        )}
        href={link}
        fluid={fluid}
        disabled={disabled}
        rounded
        size={size}
        variant={variant}
        icon={!isLoading && icon}
        iconPosition={iconPosition}
        theme={theme}
        onClick={disabled ? undefined : onClick}>
        {isLoading ? (
          <Spinner color="brand-white" size={24} withOverlay={false} />
        ) : (
          <span>{label}</span>
        )}
      </Button>
    );
  }
  return (
    <Button
      className={clsx(styles.button, className)}
      href={link}
      fluid={fluid}
      disabled={disableLink}
      rounded>
      {label}
    </Button>
  );
};

export const CustomSectionHeadline: React.FC<{
  className?: string;
  text: string;
  size?: '2' | '3';
}> = ({ className, text, size = '2' }) => (
  <Typography
    className={className}
    component={`h${size}`}
    variant="headlineSans"
    size={size}
    noPadding>
    {text}
  </Typography>
);
